import {
  Box,
  BoxProps,
  RadioGroup,
  SimpleGrid,
  SimpleGridProps,
} from '@chakra-ui/react';
import { ReactNode, forwardRef } from 'react';

interface RadioStyleButtonGroupProps {
  name: string;
  options: { label: string | ReactNode; value: string }[];
  isDisabled?: boolean;
  value?: string;
  defaultValue?: string;
  containerProps?: SimpleGridProps;
  buttonContainerProps?: BoxProps;
  buttonProps?: BoxProps;
  onChange?: (value: string) => void;
}

/*
 * v3 port: `useRadio`/`useRadioGroup` (v2's headless low-level hooks, docs
 * example this component was modeled on) are fully removed with no direct
 * replacement — Chakra v3 has no low-level hooks at all, only the real
 * compound components. This design has no separate circle mark (the whole
 * button *is* the control), so it composes `RadioGroup.Root`/`Item` directly
 * rather than reaching for `ItemControl`/`ItemIndicator`: `RadioGroup.Item`
 * renders as a real `<label>` and (confirmed against
 * `@zag-js/radio-group`'s `getItemProps`) carries `data-state="checked"`
 * itself, so `_checked` below targets the item directly, no extra data
 * plumbing needed. `RadioGroup.ItemHiddenInput` supplies the actual native
 * radio input Item's label wraps.
 */

/**
 * This component is a grid of radio buttons that are styled as buttons.
 *
 * Reference https://chakra-ui.com/docs/components/radio-group
 */
export const RadioStyleButtonGroup = forwardRef<
  HTMLDivElement,
  RadioStyleButtonGroupProps
>(
  (
    {
      name,
      options,
      defaultValue,
      isDisabled,
      value,
      buttonProps,
      buttonContainerProps,
      containerProps,
      onChange,
    },
    ref,
  ) => (
    <RadioGroup.Root
      ref={ref}
      name={name}
      disabled={isDisabled}
      value={value}
      defaultValue={defaultValue}
      onValueChange={({ value: nextValue }) => {
        if (nextValue != null) {
          onChange?.(nextValue);
        }
      }}
      w="100%"
    >
      <SimpleGrid gap={0} columns={options.length} {...containerProps}>
        {options.map(({ label, value: optionValue }, index) => (
          <RadioStyleButton
            key={optionValue}
            value={optionValue}
            disabled={isDisabled}
            buttonProps={buttonProps}
            buttonContainerProps={buttonContainerProps}
            columns={(containerProps?.columns as number) || options.length}
            index={index}
            itemCount={options.length}
          >
            {label}
          </RadioStyleButton>
        ))}
      </SimpleGrid>
    </RadioGroup.Root>
  ),
);

type RadioStyleButtonProps = RadioGroup.ItemProps & {
  buttonProps?: BoxProps;
  buttonContainerProps?: BoxProps;
  columns: number;
  index: number;
  itemCount: number;
};

export const RadioStyleButton = forwardRef<
  HTMLDivElement,
  RadioStyleButtonProps
>((props, ref) => {
  const {
    buttonProps,
    buttonContainerProps,
    columns,
    index,
    itemCount,
    children,
    ...itemProps
  } = props;

  const isFirstItem = index === 0;
  const isLastItemInFirstRow = index === columns - 1;
  const isFirstItemInFinalRow = index === itemCount - columns;
  const isLastItem = index === itemCount - 1;
  const isFirstItemInRow = index % columns === 0;
  const isFinalRow = index >= itemCount - columns;

  const topLeftBorderRadius = isFirstItem ? '2px' : '0px';
  const topRightBorderRadius = isLastItemInFirstRow ? '2px' : '0px';
  const bottomLeftBorderRadius = isFirstItemInFinalRow ? '2px' : '0px';
  const bottomRightBorderRadius = isLastItem ? '2px' : '0px';

  const showLeftBorder = isFirstItem || isFirstItemInRow;
  const showBottomBorder = isFinalRow;

  return (
    <Box flexGrow={1} textAlign="center" {...buttonContainerProps}>
      <RadioGroup.Item
        ref={ref}
        {...itemProps}
        position="relative"
        cursor="pointer"
        bg="tWhite.base"
        color="tRoyalBlue.base"
        border="1px solid"
        borderLeftColor={showLeftBorder ? 'tRoyalBlue.base' : 'transparent'}
        borderTopColor="tRoyalBlue.base"
        borderRightColor="tRoyalBlue.base"
        borderBottomColor={showBottomBorder ? 'tRoyalBlue.base' : 'transparent'}
        lineHeight="0"
        borderRadius={`${topLeftBorderRadius} ${topRightBorderRadius} ${bottomRightBorderRadius} ${bottomLeftBorderRadius}`}
        fontWeight="bold"
        h="32px"
        fontSize="xs"
        justifyContent="center"
        alignItems="center"
        display="flex"
        _checked={{
          bg: 'tBlue.hover',
          // Ensure no transparent borders when checked otherwise outline looks weird
          borderColor: 'tRoyalBlue.base',
          outline: '2.5px solid',
          outlineColor: 'tIndigo.base',
          zIndex: 1000,
        }}
        _hover={{
          bg: 'tBlue.hover',
          _disabled: {
            bg: 'tGray.lightPurple',
          },
        }}
        _disabled={{
          bg: 'tGray.lightPurple',
          color: 'tGray.darkPurple',
          border: '1px solid',
          borderColor: 'tWhite.base',
          outline: 'none',
          cursor: 'not-allowed',
        }}
        _focus={{
          boxShadow: '0px 2px 8px 0px #18243C66',
        }}
        {...buttonProps}
      >
        <RadioGroup.ItemHiddenInput />
        {children}
      </RadioGroup.Item>
    </Box>
  );
});
